import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, X, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const STATUS_BADGE = {
  new: "bg-zinc-100 text-zinc-800 border-zinc-300",
  contacted: "bg-blue-50 text-blue-800 border-blue-200",
  qualified: "bg-amber-50 text-amber-800 border-amber-200",
  proposal: "bg-violet-50 text-violet-800 border-violet-200",
  won: "bg-green-50 text-green-800 border-green-200",
  lost: "bg-red-50 text-red-800 border-red-200",
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", service: "", value: 0, notes: "", assignee_id: "" });

  const load = async () => {
    const [l, u] = await Promise.all([
      api.get("/leads"),
      api.get("/users").catch(() => ({ data: [] })),
    ]);
    setLeads(l.data);
    setUsers(u.data);
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { ...form, value: parseFloat(form.value || 0) });
      toast.success("Lead added");
      setForm({ name: "", company: "", email: "", phone: "", service: "", value: 0, notes: "", assignee_id: "" });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const filtered = leads.filter((l) =>
    [l.name, l.company, l.phone, l.email].join(" ").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen" data-testid="leads-page">
      <header className="border-b border-zinc-200 page-px py-6 sm:py-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-uppercase mb-2">Pipeline</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tighter" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Leads</h1>
        </div>
        <button
          onClick={() => setShowForm(true)}
          data-testid="new-lead-button"
          className="bg-ikb text-white px-4 py-2.5 text-sm font-medium hover:bg-[#00227A] flex items-center gap-2"
        >
          <Plus size={14} /> New Lead
        </button>
      </header>

      <div className="page-px py-4 border-b border-zinc-200 flex items-center gap-3">
        <Search size={14} className="text-zinc-400" />
        <input
          data-testid="leads-search"
          placeholder="Search by name, company, phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 bg-transparent outline-none text-sm py-1"
        />
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-zinc-300 w-full max-w-2xl p-8" data-testid="lead-form">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold tracking-tight">New Lead</h2>
              <button onClick={() => setShowForm(false)}><X size={18} /></button>
            </div>
            <form onSubmit={create} className="grid grid-cols-2 gap-4">
              <Field label="Name *">
                <input data-testid="lead-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" required />
              </Field>
              <Field label="Company">
                <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input" />
              </Field>
              <Field label="Phone *">
                <input data-testid="lead-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" placeholder="+91…" required />
              </Field>
              <Field label="Email">
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" />
              </Field>
              <Field label="Service">
                <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="input">
                  <option value="">—</option>
                  <option>Tax Filing</option>
                  <option>GST Registration</option>
                  <option>Audit</option>
                  <option>Bookkeeping</option>
                  <option>Company Formation</option>
                  <option>Compliance</option>
                </select>
              </Field>
              <Field label="Estimated Value (₹)">
                <input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="input" />
              </Field>
              <Field label="Assignee">
                <select value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })} className="input">
                  <option value="">— Unassigned —</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </Field>
              <div className="col-span-2">
                <Field label="Notes">
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input min-h-[70px]" />
                </Field>
              </div>
              <div className="col-span-2">
                <button type="submit" data-testid="lead-submit-button" className="w-full bg-ikb text-white py-2.5 text-sm font-medium">Create Lead</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="border-b border-zinc-200 overflow-x-auto">
        <div className="min-w-[700px]">
        <div className="grid grid-cols-12 label-uppercase border-b border-zinc-200 page-px py-2 bg-zinc-50">
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Company</div>
          <div className="col-span-2">Phone</div>
          <div className="col-span-2">Service</div>
          <div className="col-span-1 text-right">Value</div>
          <div className="col-span-2">Status</div>
        </div>
        {filtered.length === 0 && (
          <div className="page-px py-12 text-sm text-zinc-500 text-center">No leads found.</div>
        )}
        {filtered.map((l) => (
          <Link
            to={`/leads/${l.id}`}
            key={l.id}
            data-testid={`lead-row-${l.id}`}
            className="grid grid-cols-12 page-px py-3 border-b border-zinc-200 text-sm hover:bg-zinc-50"
          >
            <div className="col-span-3 font-medium">{l.name}</div>
            <div className="col-span-2 text-zinc-600 truncate">{l.company || "—"}</div>
            <div className="col-span-2 font-mono text-xs">{l.phone}</div>
            <div className="col-span-2 text-zinc-600">{l.service || "—"}</div>
            <div className="col-span-1 text-right font-mono">₹{(l.value || 0).toLocaleString()}</div>
            <div className="col-span-2">
              <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${STATUS_BADGE[l.status] || STATUS_BADGE.new}`}>
                {l.status}
              </span>
            </div>
          </Link>
        ))}
        </div>
      </div>

      <style>{`.input { width: 100%; border: 1px solid #d4d4d8; padding: 0.5rem 0.75rem; font-size: 0.875rem; outline: none; }
        .input:focus { border-color: #002FA7; box-shadow: 0 0 0 2px rgba(0,47,167,0.2); }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-uppercase block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
